import React from 'react';
import { Globe2, Bed, UtensilsCrossed, Sparkles, Route, ShieldCheck } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { formatMoney } from '../../utils/format';
import { ABROAD_COSTS, getAbroadDailyCost } from '../../data/competitors';

const ABROAD_ICONS = {
  bed: Bed,
  utensils: UtensilsCrossed,
  sparkles: Sparkles,
  road: Route,
  shield: ShieldCheck,
};

/**
 * Finance — onglet « Coûts export ».
 * Déplacé depuis /marché (28/05/2026 v2) — la fiscalité de l'export et
 * les frais de déplacement à l'étranger relèvent du pilotage financier,
 * pas de la veille concurrentielle.
 *
 * Affiche le détail des frais journaliers par chauffeur en mission
 * export + la liste des contrats export en cours qui en bénéficient.
 */
export default function ExportCostsTab() {
  const activeContracts = useGameStore(s => s.activeContracts || EMPTY_ARRAY);
  const abroadDailyCost = getAbroadDailyCost();
  const exportContracts = activeContracts.filter(
    c => c.category === 'Export EU' || c.isExport
  );

  return (
    <div className="space-y-4" data-testid="export-costs-tab">
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-purple-400" />
          Frais de déplacement à l&apos;étranger
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Coûts journaliers par chauffeur en mission export. Ces frais s&apos;ajoutent
          aux charges normales et impactent directement votre marge sur les contrats
          internationaux.
        </p>
        <div className="space-y-2">
          {Object.entries(ABROAD_COSTS).map(([key, cost]) => {
            const Icon = ABROAD_ICONS[cost.icon] || Globe2;
            return (
              <div
                key={key}
                className="flex items-center justify-between p-2.5 rounded-lg bg-ink-700/30"
                data-testid={`export-cost-${key}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-500" />
                  <span className="text-xs text-slate-300">{cost.label}</span>
                </div>
                <span className="text-xs font-bold text-danger">
                  {formatMoney(cost.perDay)}/jour
                </span>
              </div>
            );
          })}
          <div
            className="flex items-center justify-between p-3 rounded-lg bg-danger/10 border border-danger/40 mt-2"
            data-testid="export-cost-total"
          >
            <p className="text-sm font-semibold text-slate-200">
              Total journalier par chauffeur
            </p>
            <p className="text-lg font-bold text-danger">
              {formatMoney(abroadDailyCost)}/jour
            </p>
          </div>
        </div>
      </div>

      {exportContracts.length > 0 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3">
            Contrats export actifs ({exportContracts.length})
          </h3>
          <div className="space-y-1">
            {exportContracts.map((c) => (
              <div
                key={c.instanceId || c.name}
                className="flex items-center justify-between p-2 rounded-lg border border-ink-600"
                data-testid={`export-contract-${c.instanceId || c.name}`}
              >
                <span className="text-xs text-slate-300">{c.name}</span>
                <span className="text-xs text-danger font-medium">
                  +{formatMoney(abroadDailyCost)}/j en frais
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {exportContracts.length === 0 && (
        <div className="bg-ink-800/50 rounded-lg border border-ink-600 p-4 text-center">
          <p className="text-xs text-slate-500 italic">
            Aucun contrat export actif. Les contrats « Export EU » apparaissent
            dans la page Contrats une fois le 3ᵉ dépôt débloqué.
          </p>
        </div>
      )}
    </div>
  );
}
